import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Building2,
  CalendarDays,
  CircleDollarSign,
  CreditCard,
  FileText,
  TrendingUp
} from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { AppContext } from '../../context/AppContext';
import { useCurrency } from '../../utils/currencyUtils';
import { buildFinanceModel, getDateRange, toDate } from './financeReporting';

const FinanceDashboard = () => {
  const { currentUser } = useAuth();
  const { activeBusiness } = useContext(AppContext);
  const { formatAmount } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [datePreset, setDatePreset] = useState('month');
  const [selectedBusinessId, setSelectedBusinessId] = useState('all');

  useEffect(() => {
    if (activeBusiness?.id) {
      setSelectedBusinessId(activeBusiness.id);
    }
  }, [activeBusiness?.id]);

  useEffect(() => {
    const fetchFinanceData = async () => {
      if (!currentUser?.uid) return;
      setLoading(true);
      try {
        const [businessSnap, accountSnap, txSnap] = await Promise.all([
          getDocs(query(collection(db, 'businesses'), where('createdBy', '==', currentUser.uid))),
          getDocs(query(collection(db, 'accounts'), where('createdBy', '==', currentUser.uid))),
          getDocs(query(collection(db, 'financialTransactions'), where('createdBy', '==', currentUser.uid)))
        ]);

        setBusinesses(businessSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setAccounts(accountSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setTransactions(txSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error('Error loading finance dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceData();
  }, [currentUser?.uid]);

  const filteredAccounts = useMemo(() => {
    if (selectedBusinessId === 'all') return accounts;
    return accounts.filter((account) => account.businessId === selectedBusinessId);
  }, [accounts, selectedBusinessId]);

  const filteredTransactions = useMemo(() => {
    if (selectedBusinessId === 'all') return transactions;
    return transactions.filter((tx) => tx.businessId === selectedBusinessId);
  }, [transactions, selectedBusinessId]);

  const { start, end } = getDateRange(datePreset);
  const model = useMemo(() => (
    buildFinanceModel({
      transactions: filteredTransactions,
      accounts: filteredAccounts,
      rangeStart: start,
      rangeEnd: end,
      now: new Date()
    })
  ), [filteredTransactions, filteredAccounts, start, end]);

  const maxTrend = Math.max(...model.monthlyNet.map((m) => Math.abs(m.net)), 1);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-28 bg-gray-100 rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Finance Dashboard</h2>
            <p className="text-sm text-gray-500 mt-1">
              {start.toLocaleDateString()} - {end.toLocaleDateString()}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedBusinessId}
              onChange={(event) => setSelectedBusinessId(event.target.value)}
            >
              <option value="all">All businesses</option>
              {businesses.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.name || business.businessName || 'Untitled Business'}
                </option>
              ))}
            </select>
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={datePreset}
              onChange={(event) => setDatePreset(event.target.value)}
            >
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="quarter">This quarter</option>
              <option value="year">This year</option>
            </select>
            <Link
              to="/finance/reports"
              className="inline-flex items-center px-3 py-2 rounded-lg bg-[#190192] text-white text-sm font-medium hover:bg-[#2C0DB5]"
            >
              <BarChart3 size={16} className="mr-2" />
              View Reports
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-md p-5">
          <p className="text-sm text-gray-500">Net Profit</p>
          <p className={`text-2xl font-bold mt-1 ${model.current.netProfit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
            {formatAmount(model.current.netProfit)}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Income {formatAmount(model.current.income)} | Expenses {formatAmount(model.current.expenses)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5">
          <p className="text-sm text-gray-500">Cash On Hand</p>
          <p className="text-2xl font-bold mt-1 text-gray-900">{formatAmount(model.cash.onHand)}</p>
          <p className="text-xs text-gray-500 mt-2">
            Cash Flow: {formatAmount(model.cash.net)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5">
          <p className="text-sm text-gray-500">Open Receivables</p>
          <p className="text-2xl font-bold mt-1 text-blue-700">{formatAmount(model.receivables.outstanding)}</p>
          <p className="text-xs text-gray-500 mt-2">
            {model.receivables.openCount} open | Overdue {formatAmount(model.receivables.overdue)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-5">
          <p className="text-sm text-gray-500">Open Payables</p>
          <p className="text-2xl font-bold mt-1 text-amber-700">{formatAmount(model.payables.outstanding)}</p>
          <p className="text-xs text-gray-500 mt-2">
            {model.payables.openCount} open | Overdue {formatAmount(model.payables.overdue)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">6-Month Net Trend</h3>
          <div className="space-y-3">
            {model.monthlyNet.map((month) => {
              const width = `${Math.max(6, Math.round((Math.abs(month.net) / maxTrend) * 100))}%`;
              const positive = month.net >= 0;
              return (
                <div key={month.label}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">{month.label}</span>
                    <span className={positive ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}>
                      {formatAmount(month.net)}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`${positive ? 'bg-green-500' : 'bg-red-500'} h-2`} style={{ width }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Link to="/finance/new" className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
              <span className="flex items-center text-sm text-gray-700"><CircleDollarSign size={16} className="mr-2 text-gray-500" />New Transaction</span>
              <ArrowUpRight size={14} className="text-gray-400" />
            </Link>
            <Link to="/finance/accounts" className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
              <span className="flex items-center text-sm text-gray-700"><CreditCard size={16} className="mr-2 text-gray-500" />Manage Accounts</span>
              <ArrowUpRight size={14} className="text-gray-400" />
            </Link>
            <Link to="/finance/reports/profit-loss" className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
              <span className="flex items-center text-sm text-gray-700"><FileText size={16} className="mr-2 text-gray-500" />Profit & Loss</span>
              <ArrowUpRight size={14} className="text-gray-400" />
            </Link>
            <Link to="/finance/reports/receivables" className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
              <span className="flex items-center text-sm text-gray-700"><AlertTriangle size={16} className="mr-2 text-gray-500" />Receivables Aging</span>
              <ArrowUpRight size={14} className="text-gray-400" />
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h3>
          <div className="space-y-3">
            {model.recentTransactions.length === 0 && (
              <p className="text-sm text-gray-500">No transactions available yet.</p>
            )}
            {model.recentTransactions.map((tx) => {
              const amount = tx.type === 'simple' ? Number(tx.amount || 0) : Number(tx.totalAmount || tx.balanceDue || 0);
              const positive = tx.type === 'simple' ? amount > 0 : tx.type === 'invoice';
              return (
                <div key={tx.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{tx.description || tx.documentNumber || tx.id}</p>
                    <p className="text-xs text-gray-500">
                      {(tx._date || toDate(tx.date) || new Date()).toLocaleDateString()} | {tx.type || 'transaction'}
                    </p>
                  </div>
                  <p className={`text-sm font-semibold ${positive ? 'text-green-700' : 'text-red-700'}`}>
                    {positive ? '+' : '-'}{formatAmount(Math.abs(amount))}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Period Health</h3>
          <div className="space-y-4">
            <div className="flex items-start justify-between p-3 rounded-lg bg-gray-50">
              <div className="flex items-center">
                <TrendingUp size={16} className="mr-2 text-gray-600" />
                <span className="text-sm text-gray-700">Income change</span>
              </div>
              <span className={`text-sm font-semibold ${model.changes.income >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {model.changes.income >= 0 ? '+' : ''}{model.changes.income}%
              </span>
            </div>
            <div className="flex items-start justify-between p-3 rounded-lg bg-gray-50">
              <div className="flex items-center">
                <ArrowDownRight size={16} className="mr-2 text-gray-600" />
                <span className="text-sm text-gray-700">Expense change</span>
              </div>
              <span className={`text-sm font-semibold ${model.changes.expenses <= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {model.changes.expenses >= 0 ? '+' : ''}{model.changes.expenses}%
              </span>
            </div>
            <div className="flex items-start justify-between p-3 rounded-lg bg-gray-50">
              <div className="flex items-center">
                <CalendarDays size={16} className="mr-2 text-gray-600" />
                <span className="text-sm text-gray-700">Period transactions</span>
              </div>
              <span className="text-sm font-semibold text-gray-900">{model.counts.transactionsInPeriod}</span>
            </div>
            <div className="flex items-start justify-between p-3 rounded-lg bg-gray-50">
              <div className="flex items-center">
                <Building2 size={16} className="mr-2 text-gray-600" />
                <span className="text-sm text-gray-700">Tax liability</span>
              </div>
              <span className={`text-sm font-semibold ${model.tax.liability >= 0 ? 'text-amber-700' : 'text-green-700'}`}>
                {formatAmount(model.tax.liability)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;
